import Experience from "@folio-2026/experience"
import Section from "@folio-2026/section"
import { EVENTS, supabase } from "@folio-2026/shared"
import { TextScene } from "@folio-2026/text"
import BloomNode, { bloom } from "three/examples/jsm/tsl/display/BloomNode.js"
import { color, emissive, mrt, output, pass, uniform } from "three/tsl"
import * as THREE from "three/webgpu"

import GuestbookSectionCamera from "./Camera"
import Floor from "./Floor"
import Grid from "./Grid"
import GridLines from "./GridLines"
import GridParticles from "./GridParticles"
import Man from "./Man"
import Memories from "./Memories"
import sources from "./sources"
import { useGuestbookStore } from "./store"

// Number of available face textures
const FACE_COUNT = 5
// Array of face texture resource names
const FACE_TEXTURES = Array.from(
  { length: FACE_COUNT },
  (_, i) => `face-${i + 1}`,
)
// Maximum number of memories to show in the guestbook
const MAX_MEMORIES = 100

export default class GuestbookSection extends Section {
  name = "guestbook"

  experience: Experience
  sizes: Experience["sizes"]
  grid!: Grid
  man!: Man
  cube!: THREE.Mesh<THREE.BoxGeometry, THREE.MeshLambertMaterial>
  gridParticles: GridParticles[] = []
  memories!: Memories

  postProcessing!: THREE.PostProcessing

  declare camera: GuestbookSectionCamera

  // Cached text references
  private navigationTexts: TextScene["texts"] = []
  private scrollToContinueText?: TextScene["texts"][number]
  private scrollToContinueTimeout?: ReturnType<typeof setTimeout>
  private scrollWheelHandler?: () => void
  private scrollTouchStartHandler?: (e: TouchEvent) => void
  private scrollTouchMoveHandler?: (e: TouchEvent) => void

  settings = {
    spacing: 0.5,
    backgroundColor: uniform(color("#020c15")),
  }

  constructor() {
    super({
      name: "guestbook",
      sources: sources,
      folderName: "💬 Guestbook scene",
      expanded: false,
    })

    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes

    this.setDebug()
  }

  init() {
    super.init()

    this.camera = new GuestbookSectionCamera({ scene: this.scene })
    this.setupCamera()

    this.setup()
    this.setPostProcessing()

    this.fetchMemories()
  }

  eventListeners() {
    super.eventListeners()
    this.experience.on(EVENTS.Scroll.Section.StartUpdate, (e: unknown) => {
      const { name, direction } = e as { name: string; direction: -1 | 0 | 1 }
      if (name !== this.name || direction === -1) {
        return
      }
      console.log("RESET NAVIGATION TEXT")
      this.resetNavigationText()
    })

    this.experience.on(
      EVENTS.Guestbook.BeforeEnter,
      this.beforeEnterAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.enteringAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Guestbook.Exiting,
      this.exitingAnimation.bind(this),
    )

    this.experience.on(EVENTS.Guestbook.Entered, this.onEntered.bind(this))
    this.experience.on(EVENTS.Guestbook.Memory.Add, this.onMemoryAdd.bind(this))

    this.experience.on(
      EVENTS.Guestbook.Memory.Close,
      this.onMemoryClose.bind(this),
    )
    this.experience.on(
      EVENTS.Guestbook.Memory.Click,
      this.onMemoryClick.bind(this),
    )

    this.experience.on(EVENTS.Audio.SetVolume, (e: unknown) => {
      const { volume } = e as { volume: number }
      if (this.experience.scrollInfo.currentSection?.name === this.name) {
        this.setVolume(volume)
      }
    })
  }

  beforeEnterAnimation() {
    if (!this.audio) {
      this.createAudio("guestbook-soundtrack")
    }
    this.playAudio()
  }

  enteringAnimation(e: unknown) {
    const { progress } = e as { progress: number }
    const volumeLevel = progress * this.experience.volume
    this.setVolume(volumeLevel)
  }

  exitingAnimation(e: unknown) {
    const { progress } = e as { progress: number }
    const volumeLevel = (1 - progress) * this.experience.volume
    this.setVolume(volumeLevel)
  }

  onMemoryAdd() {
    useGuestbookStore.getState().setIsAddMemoryOpen(true)
  }

  onMemoryClick() {
    this.hideNavigationText()
    useGuestbookStore.getState().setIsAddMemoryOpen(false)

    // Set up scroll event listeners to cancel text reveal on scroll
    this.setupScrollListeners()

    // Reveal "Scroll to continue" text when memory opens
    if (this.scrollToContinueText) {
      this.scrollToContinueTimeout = setTimeout(() => {
        // Only reveal if the timeout hasn't been cancelled
        if (this.scrollToContinueText) {
          this.scrollToContinueText.reveal()
        }
        this.cleanupScrollListeners()
        this.scrollToContinueTimeout = undefined
      }, 2500)
    }
  }

  onMemoryClose() {
    // Cancel the reveal timeout if it's still pending
    if (this.scrollToContinueTimeout) {
      clearTimeout(this.scrollToContinueTimeout)
      this.scrollToContinueTimeout = undefined
    }

    // Clean up scroll listeners
    this.cleanupScrollListeners()

    // Hide "Scroll to continue" text when memory closes
    if (this.scrollToContinueText) {
      this.scrollToContinueText.leave()
    }

    this.revealNavigationText()
  }

  private setupScrollListeners() {
    const domElement = this.experience.renderer.instance.domElement

    // Create bound handlers for proper cleanup
    this.scrollWheelHandler = this.handleScroll.bind(this)
    this.scrollTouchStartHandler = this.handleTouchStart.bind(this)
    this.scrollTouchMoveHandler = this.handleTouchMove.bind(this)

    // Add wheel event for desktop
    domElement.addEventListener("wheel", this.scrollWheelHandler, {
      passive: true,
    })
    // Add touch events for mobile
    domElement.addEventListener("touchstart", this.scrollTouchStartHandler, {
      passive: true,
    })
    domElement.addEventListener("touchmove", this.scrollTouchMoveHandler, {
      passive: true,
    })
  }

  private cleanupScrollListeners() {
    const domElement = this.experience.renderer.instance.domElement

    if (this.scrollWheelHandler) {
      domElement.removeEventListener("wheel", this.scrollWheelHandler)
      this.scrollWheelHandler = undefined
    }

    if (this.scrollTouchStartHandler) {
      domElement.removeEventListener("touchstart", this.scrollTouchStartHandler)
      this.scrollTouchStartHandler = undefined
    }

    if (this.scrollTouchMoveHandler) {
      domElement.removeEventListener("touchmove", this.scrollTouchMoveHandler)
      this.scrollTouchMoveHandler = undefined
    }
  }

  private handleScroll() {
    // Cancel the text reveal timeout when user scrolls
    if (this.scrollToContinueTimeout) {
      clearTimeout(this.scrollToContinueTimeout)
      this.scrollToContinueTimeout = undefined
      this.cleanupScrollListeners()
    }
  }

  private handleTouchStart(_e: TouchEvent) {
    // Track touch start for potential scroll detection
    // We'll detect actual scrolling in touchmove
  }

  private handleTouchMove(_e: TouchEvent) {
    // Cancel the text reveal timeout when user scrolls (touch)
    if (this.scrollToContinueTimeout) {
      clearTimeout(this.scrollToContinueTimeout)
      this.scrollToContinueTimeout = undefined
      this.cleanupScrollListeners()
    }
  }

  onEntered() {
    this.positionTexts()
    this.cacheTexts() // Cache texts after they're created and positioned
    this.revealNavigationText()
  }

  private cacheTexts() {
    // Cache navigation texts (excluding "Scroll to continue")
    this.navigationTexts = this.textScene.texts.filter(
      (text) => text.name === "guestbook" && text.text !== "Scroll to continue",
    )

    // Cache "Scroll to continue" text
    this.scrollToContinueText = this.textScene.texts.find(
      (text) => text.name === "guestbook" && text.text === "Scroll to continue",
    )
  }

  resetNavigationText() {
    this.navigationTexts.forEach((text: TextScene["texts"][number]) => {
      text.revertReveal()
    })
    this.textScene.smallButtons
      .filter((button) => button.id === "guestbook-memory-add")
      .forEach((button) => button.resetToRevealStart())
  }

  revealNavigationText() {
    this.navigationTexts.forEach((text: TextScene["texts"][number]) => {
      text.reveal()
    })

    this.textScene.smallButtons
      .filter((button) => button.id === "guestbook-memory-add")
      .forEach((button) => button.reveal())
  }

  hideNavigationText() {
    this.navigationTexts.forEach((text: TextScene["texts"][number]) => {
      text.leave()
    })

    this.textScene.smallButtons
      .filter((button) => button.id === "guestbook-memory-add")
      .forEach((button) => button.leave())
  }

  positionTexts() {
    const guestbookTexts = this.textScene.texts.filter(
      (text) => text.name === "guestbook",
    )
    guestbookTexts.forEach((text) => {
      const bounds = text.element.getBoundingClientRect()
      text.y = bounds.top

      const positionY = -text.y + this.sizes.height / 2 - bounds.height

      const positionX = bounds.left - this.sizes.width / 2

      text.mesh.position.y = positionY
      text.mesh.position.x = positionX
    })
  }

  async fetchMemories() {
    const client = await supabase()
    const { data } = await client.from("guestbook").select()

    if (data) {
      // Shuffle and cap to MAX_MEMORIES (Fisher–Yates shuffle)
      const shuffled = [...data]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }
      const memories = shuffled.slice(0, MAX_MEMORIES)

      // Collect all face textures into an array
      const faceDepthTextures = FACE_TEXTURES.map(
        (key) => this.resources.items[key] as THREE.Texture,
      )

      this.memories = new Memories({
        scene: this.scene,
        debugFolder: this.debugFolder,
        spacing: this.settings.spacing,
        memories,
        camera: this.camera.camera,
        perlinTexture: this.experience.globalResources.perlin as THREE.Texture,
        faceDepthTextures,
      })
    }
  }

  setupCamera() {
    this.camera.camera.name = "guestbook-camera"
  }

  setup() {
    /**
     * GRID
     */
    const perlin = this.experience.globalResources.perlin as THREE.Texture
    perlin.wrapS = THREE.RepeatWrapping
    perlin.wrapT = THREE.RepeatWrapping
    perlin.minFilter = THREE.LinearFilter
    const grid = new Grid({
      scene: this.scene,
      debugFolder: this.debugFolder,
      spacing: this.settings.spacing,
      gridColor: new THREE.Color("#2b414c"),
      perlin,
    })
    this.grid = grid

    /**
     * GRID LINES
     */
    new GridLines({
      scene: this.scene,
      debugFolder: this.debugFolder,
      grid,
    })

    /**
     * GRID PARTICLES
     */

    this.gridParticles.push(
      new GridParticles({
        name: "far-to-near",
        scene: this.scene,
        grid,
        debugFolder: this.debugFolder,
        count: 15,
        direction: "far-to-near",
        position: { x: 8, y: 7 },
        speed: 1.3,
      }),
    )

    this.gridParticles.push(
      new GridParticles({
        name: "right-to-left",
        scene: this.scene,
        grid,
        debugFolder: this.debugFolder,
        count: 25,
        direction: "right-to-left",
        position: { y: 4, z: 30 },
        speed: 0.5,
      }),
    )

    /**
     * FLOOR
     */
    new Floor({
      scene: this.scene,
      debugFolder: this.debugFolder,
      color: this.settings.backgroundColor,
    })

    /**
     * MAN
     */
    this.man = new Man({
      scene: this.scene,
      debugFolder: this.debugFolder,
      resources: {
        ...this.experience.globalResources,
        ...this.resources.items,
      },
    })

    // Add axes helper
    // const axesHelper = new THREE.AxesHelper(5)
    // this.scene.add(axesHelper)
  }

  setPostProcessing(): void {
    super.setPostProcessing()

    this.postProcessing = new THREE.PostProcessing(this.renderer.instance)
    /**
     * Whether the default output tone mapping and color
     * space transformation should be enabled or not.
     *
     * It is enabled by default by it must be disabled when
     * effects must be executed after tone mapping and color
     * space conversion. A typical example is FXAA which
     * requires sRGB input.
     *
     * When set to `false`, the app must control the output
     * transformation with `RenderOutputNode`.
     * */
    // this.postProcessing.outputColorTransform = false

    const scenePass = pass(this.scene, this.camera.camera)
    scenePass.setMRT(
      mrt({
        output,
        emissive,
      }),
    )

    const outputPass = scenePass.getTextureNode()
    const emissivePass = scenePass.getTextureNode("emissive")

    const bloomPass = bloom(emissivePass, 2.5, 0.5)

    this.postProcessing.outputNode = outputPass
      .add(bloomPass)
      .add(this.textScene.postProcessing.outputNode)

    this.setPPDebug({ bloomPass })
  }

  setPPDebug({ bloomPass }: { bloomPass: BloomNode }) {
    if (!this.debugFolder) {
      return
    }

    const pPFolder = this.debugFolder!.addFolder({
      title: "💅 Post Processing",
      expanded: false,
    })

    pPFolder.addBinding(bloomPass.strength, "value", {
      label: "Bloom strength",
      min: 0,
      max: 3,
      step: 0.001,
    })

    pPFolder.addBinding(bloomPass.radius, "value", {
      label: "Bloom radius",
      min: 0,
      max: 1,
      step: 0.001,
    })

    pPFolder.addBinding(bloomPass.threshold, "value", {
      label: "Bloom threshold",
      min: 0,
      max: 1,
      step: 0.001,
    })
  }

  resize() {
    super.resize()

    this.positionTexts()
  }

  update(params: { delta: number; elapsed: number }) {
    super.update(params)

    if (this.camera) {
      this.camera.update()
    }

    if (this.man) {
      this.man.update(params)
    }

    this.gridParticles.forEach((particles) => {
      particles.update()
    })

    if (this.memories) {
      this.memories.update()
    }

    if (this.postProcessing) {
      this.postProcessing.render()
    }
  }

  setDebug() {
    super.setDebug()

    if (!this.debugFolder) {
      return
    }

    const settings = {
      backgroundColor: `#${new THREE.Color(this.settings.backgroundColor.value).getHexString()}`,
    }

    this.debugFolder
      .addBinding(settings, "backgroundColor", {
        label: "Background color",
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.backgroundColor.value = new THREE.Color(ev.value)
        this.scene.background = new THREE.Color(ev.value)
      })
  }
}
